import { useAuthContext } from "BEDROCK/auth/auth-provider";
import { useQuery } from "@tanstack/react-query";
import { ShippingAddress } from "entity-store/address-store/use-address-store";

export type OrderShippingAddress = Omit<ShippingAddress, "id" | "user_id" | "is_default" | "created_at" | "updated_at">;

export interface Order {
  id: string;
  created_at: string;
  totalPrice: number;
  projectIDs: string[];
  shippingAddress?: OrderShippingAddress;
}

export const useOrderStore = () => {
  const { client, userID } = useAuthContext();

  const getOrder = async (orderID: string): Promise<Order> => {
    if (!userID || !client)
      throw new Error("UseOrderStore 11: User not authenticated");
    const { data, error } = await client
      .from("orders")
      .select("*")
      .eq("id", orderID)
      .single();

    if (error) throw error;

    // project_ids is a comma-separated string of ids
    const projectIDs = data.project_ids
      .split(",")
      .map((id: string) => id.trim());

    const santised: Order = {
      id: data.id,
      created_at: data.created_at,
      totalPrice: data.price,
      projectIDs: projectIDs,
      shippingAddress: data.delivery_address ? {
        line1: data.delivery_address.line1,
        line2: data.delivery_address.line2,
        city: data.delivery_address.city,
        postcode: data.delivery_address.postcode,
        country: data.delivery_address.country,
      } : undefined,
    };
    return santised;
  };

  const getAllOrdersForUser = async (): Promise<Order[]> => {
    if (!userID || !client)
      throw new Error("UseOrderStore: User not authenticated");

    const { data, error } = await client
      .from("orders")
      .select("*")
      .eq("user_id", userID)
      .order("created_at", { ascending: false });

    if (error) throw error;

    const orders: Order[] = data.map((row: any) => ({
      id: row.id,
      created_at: row.created_at,
      totalPrice: row.price,
      projectIDs: row.project_ids.split(",").map((id: string) => id.trim()),
      shippingAddress: row.delivery_address ? {
        line1: row.delivery_address.line1,
        line2: row.delivery_address.line2,
        city: row.delivery_address.city,
        postcode: row.delivery_address.postcode,
        country: row.delivery_address.country,
      } : undefined,
    }));

    return orders;
  };

  const {
    data: orders,
    refetch: refetchOrders,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ["orders", userID],
    queryFn: getAllOrdersForUser,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    enabled: !!userID,
  });

  return {
    getOrder,
    orders,
    refetchOrders,
    isLoading,
    isFetching,
    isError,
  };
};
